import BadgeWithIcon from 'tailwind-components/BadgeWithIcon/BadgeWithIcon';
import Button from 'tailwind-components/Button/Button';
import Modal from 'tailwind-components/Modal/Modal';
import PropTypes from 'prop-types';
import React from 'react';
import {
  CheckCircle2,
  ExternalLink,
  Info,
  TriangleAlert,
  UserCheck
} from 'lucide-react';
import { cn } from 'lib/utils';
import useLmsInvite, { LMS_INVITE_EXISTING_STATUS } from './useLmsInvite';

export const LMS_URL =
  'https://sunshine-life-health-madrinas-insurance.zoholearn.com/';

const INVITE_SUBJECT =
  'Invitation to join portal Sunshine Life & Health/Madrinas Insurance in Zoho Learn.';
const INVITE_SENDER = 'noreply@mail.zoholearn.com';

const INTRO_TITLE = 'You will receive an invitation email';
const INTRO_MESSAGE =
  'We will send you an invitation to join the Learning Management System (LMS). Follow the instructions in that email to create your training account, and check your spam folder if you do not see it.';

const SENT_TITLE = 'Check your inbox to accept the invitation';
const SENT_MESSAGE =
  'The email can take a few minutes to arrive. Look for the subject and sender listed below, and check your spam or junk folder if you do not see it, then follow the link in the email to create your LMS account.';

const EXISTING_TITLE = 'You already have an LMS account';
const EXISTING_MESSAGE =
  'No invitation was needed because this email address is already registered in the LMS. Use the link below to sign in with it.';

const FAILED_TITLE = 'The invitation was not sent';
const FAILED_MESSAGE =
  'No invitation email went out this time. Try again in a moment, and contact your Agent Consultant if it keeps failing.';

const fieldLabelClass =
  'text-[10px] font-semibold tracking-wide text-gray-400 uppercase';
const fieldValueClass = 'text-sm wrap-break-word text-gray-900';

// callout at the top of the modal: intro before sending, then the outcome.
// the copy is portal-side on purpose — zoho's own message goes to the alert
function getCallout(result) {
  if (!result) {
    return {
      Icon: Info,
      iconBgClass: 'bg-primary-darkened',
      boxClass: 'border-primary-darkened bg-primary-darkened/10',
      textClass: 'text-primary-darkened',
      title: INTRO_TITLE,
      message: INTRO_MESSAGE
    };
  }

  if (result.status === LMS_INVITE_EXISTING_STATUS) {
    return {
      Icon: UserCheck,
      iconBgClass: 'bg-semantic-info',
      boxClass: 'border-semantic-info bg-semantic-info/10',
      textClass: 'text-semantic-info',
      title: EXISTING_TITLE,
      message: EXISTING_MESSAGE
    };
  }

  if (result.ok) {
    return {
      Icon: CheckCircle2,
      iconBgClass: 'bg-secondary-darkened',
      boxClass: 'border-secondary-darkened bg-secondary-darkened/10',
      textClass: 'text-secondary-darkened',
      title: SENT_TITLE,
      message: SENT_MESSAGE
    };
  }

  return {
    Icon: TriangleAlert,
    iconBgClass: 'bg-semantic-danger',
    boxClass: 'border-semantic-danger bg-semantic-danger/10',
    textClass: 'text-semantic-danger',
    title: FAILED_TITLE,
    message: FAILED_MESSAGE
  };
}

/**
 * asks the agent to send themselves the Zoho Learn (LMS) invitation email and
 * offers a direct link to the LMS for agents that are already registered
 * @param onClose - closes the modal
 */
export default function LmsInviteModal({ onClose }) {
  const { agentEmail, isSending, result, sendInvite } = useLmsInvite();

  const callout = getCallout(result);
  // an already registered agent has nothing left to send either, the LMS link is the action
  const isDone = !!result?.ok || result?.status === LMS_INVITE_EXISTING_STATUS;

  return (
    <Modal
      active
      title={isSending ? 'Sending Invite...' : 'LMS Access'}
      size='medium'
      closeModal={isSending ? () => {} : onClose}
    >
      <div className='flex w-full min-w-0 flex-col gap-4'>
        <BadgeWithIcon
          className={cn('border', callout.boxClass)}
          icon={
            <div
              className={cn(
                'flex size-6 shrink-0 items-center justify-center rounded-full',
                callout.iconBgClass
              )}
              aria-hidden
            >
              <callout.Icon className='size-4 text-white' strokeWidth={2} />
            </div>
          }
        >
          <p
            className={cn(
              'text-sm font-semibold leading-snug',
              callout.textClass
            )}
          >
            {callout.title}
          </p>
          <p className={cn('text-sm leading-relaxed', callout.textClass)}>
            {callout.message}
          </p>
        </BadgeWithIcon>

        <div className='grid grid-cols-1 gap-x-5 gap-y-3 rounded-[10px] border border-gray-200 px-5 py-4 sm:grid-cols-2'>
          <div className='flex min-w-0 flex-col gap-0.5 sm:col-span-2'>
            <span className={fieldLabelClass}>Subject</span>
            <span className={fieldValueClass}>{INVITE_SUBJECT}</span>
          </div>
          <div className='flex min-w-0 flex-col gap-0.5'>
            <span className={fieldLabelClass}>From</span>
            <span className={fieldValueClass}>{INVITE_SENDER}</span>
          </div>
          {agentEmail && (
            <div className='flex min-w-0 flex-col gap-0.5'>
              <span className={fieldLabelClass}>Sent To</span>
              <span className={fieldValueClass}>{agentEmail}</span>
            </div>
          )}
        </div>

        <div className='h-px bg-gray-200' />

        <div className='flex flex-wrap items-center gap-1.5 text-sm text-gray-600'>
          <span>Already registered?</span>
          <a
            className='inline-flex items-center gap-1.5 font-semibold text-primary-emphasis-light transition-colors hover:text-primary-darkened'
            href={LMS_URL}
            target='_blank'
            rel='noreferrer'
            onClick={onClose}
          >
            Click here to go to the LMS
            <ExternalLink className='size-4 shrink-0' aria-hidden />
          </a>
        </div>

        <div className='flex w-full gap-2.5'>
          <Button
            disabled={isSending}
            onClick={onClose}
            fill
            text={isDone ? 'Close' : 'Cancel'}
            variant={isDone ? 'default' : 'danger'}
          />
          {!isDone && (
            <Button
              disabled={isSending}
              isLoading={isSending}
              onClick={sendInvite}
              fill
              text={result ? 'Try Again' : 'Send Invite'}
              variant='primaryLight'
            />
          )}
        </div>
      </div>
    </Modal>
  );
}

LmsInviteModal.propTypes = {
  onClose: PropTypes.func.isRequired
};
